import React, { useState, useEffect } from 'react'

import { Box, Tabs, Tab } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { useUser } from '../../../hooks/useUser'
import { useLeads, LeadsFilters } from '../../../hooks/Outreach/useLeads'
import { useCampaign } from '../../../hooks/Outreach/useCampaign'
import { TabPanel } from '../../../components'
import { ProspectsTab } from './ProspectsTab'
import { LeadsTab } from './LeadsTab'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        padding: theme.spacing(1),
    },
    tabsWrapper: {
        marginLeft: 0,
        marginBottom: theme.spacing(2),
        paddingLeft: 0,
    },
    tabsContainer: {
        marginBottom: 0,
        paddingLeft: 0,
        marginLeft: 0,
    },
}))

type TabValue = 0 | 1 // 0 = Prospects, 1 = Leads

export const LeadHome = () => {
    const classes = useStyles()
    const { workspaceUser } = useUser()
    const { campaigns } = useCampaign()

    const apartmentId = workspaceUser?.active_workspace.id

    const [currentTab, setCurrentTab] = useState<TabValue>(0) // Default to Prospects (0)

    const [filters, setFilters] = useState<LeadsFilters>({
        page: 1,
        page_size: 25,
        is_lead: false,
    })

    // Update filters when tab changes
    useEffect(() => {
        setFilters((prev) => ({
            ...prev,
            is_lead: currentTab === 1 ? true : false,
            page: 1, // Reset to first page when switching tabs
        }))
    }, [currentTab])

    // Fetch all data using the hook
    const {
        combinedProspects,
        combinedProspectsPagination,
        leads,
        leadsPagination,
        isLoadingCombinedProspects,
        isLoadingLeads,
        addProspectToCampaign,
        syncLead,
        approveApplication,
        isApprovingApplication,
        refetchLeads,
    } = useLeads({
        apartmentId,
        filters,
        enabled: !!apartmentId,
    })

    const handleTabChange = (
        _event: React.ChangeEvent<{}>,
        newValue: TabValue,
    ) => {
        setCurrentTab(newValue)
    }

    const handleFiltersChange = (newFilters: LeadsFilters) => {
        setFilters({
            ...newFilters,
            is_lead: currentTab === 1 ? true : false,
        })
    }

    return (
        <Box className={classes.container}>
            {/* Tabs */}
            <Box className={classes.tabsWrapper}>
                <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    className={classes.tabsContainer}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label="Prospects" />
                    <Tab label="Leads" />
                </Tabs>
            </Box>

            {/* Tab Content */}
            <TabPanel value={currentTab} index={0}>
                <ProspectsTab
                    combinedProspects={combinedProspects}
                    combinedProspectsPagination={combinedProspectsPagination}
                    isLoadingCombinedProspects={isLoadingCombinedProspects}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    campaigns={campaigns}
                    apartmentId={apartmentId}
                    addProspectToCampaign={addProspectToCampaign}
                />
            </TabPanel>
            <TabPanel value={currentTab} index={1}>
                <LeadsTab
                    leads={leads}
                    leadsPagination={leadsPagination}
                    isLoadingLeads={isLoadingLeads}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    syncLead={syncLead}
                    approveApplication={approveApplication}
                    isApprovingApplication={isApprovingApplication}
                    refetchLeads={refetchLeads}
                />
            </TabPanel>
        </Box>
    )
}
